/* ==========================================================================
   "Worlds" navigation (variant B+C)

   The markup is rendered server-side in full — every world panel, every
   category pane and every drawer level is already in the document. This script
   only decides which of them is shown, and animates the moves the design asks
   for: the panel's height change on a category swap, the change from one world
   to the other, and the drawer's three-layer slide.
   ========================================================================== */

(() => {
  const CLOSE_DELAY = 140;

  /* ------------------------------------------------------------------ *
   * Desktop panels
   * ------------------------------------------------------------------ */

  const nav = document.querySelector(".hnav");

  if (nav) {
    const tops = Array.from(nav.querySelectorAll(".hnav__top[data-world]"));
    const panels = new Map(
      Array.from(nav.querySelectorAll(".hnav__panel")).map((p) => [
        p.dataset.world,
        p,
      ]),
    );
    let closeTimer = null;
    let openWorld = null;

    /* Swap the content and travel from the old height to the new one. Reading
       offsetHeight first means a swap mid-animation continues from where the
       panel currently is, rather than restarting from the last target. A panel
       that is still hidden has no height of its own to start from, so the
       caller passes the one to travel from — the panel it replaces. */
    const withHeightTransition = (
      panel,
      update,
      from = panel.offsetHeight,
    ) => {
      panel.style.height = "auto";
      update();
      const to = panel.offsetHeight;

      if (!from || from === to) {
        panel.style.height = "auto";
        return;
      }

      panel.style.height = `${from}px`;
      void panel.offsetHeight; // force a reflow, otherwise the start is dropped
      panel.style.height = `${to}px`;
    };

    /* Back to auto afterwards, so later layout changes (a window resize, say)
       do not run against a frozen pixel height. */
    panels.forEach((panel) => {
      panel.addEventListener("transitionend", (e) => {
        if (e.target !== panel || e.propertyName !== "height") return;
        // Only release when this transition really reached its target — a late
        // event would otherwise cut off an animation that has since started.
        if (Math.round(parseFloat(panel.style.height)) === panel.offsetHeight) {
          panel.style.height = "auto";
        }
      });
    });

    const showPane = (panel, index) => {
      panel.querySelectorAll(".hnav__rail-item").forEach((item) => {
        item.dataset.on = item.dataset.i === String(index) ? "true" : "false";
      });
      panel.querySelectorAll(".hnav__pane").forEach((pane) => {
        pane.toggleAttribute("hidden", pane.dataset.i !== String(index));
      });
    };

    /* Changing from one world to the other. `data-enter` names the side the
       newcomer arrives from — the side its tab sits on relative to the tab that
       was open — and the stylesheet slides the panel's content and the tab's
       underline in from there. It is cleared on every open and close: a panel
       hidden mid-animation never fires animationend, and would otherwise replay
       the animation the next time it opens from closed. */
    const clearEnter = () => {
      panels.forEach((panel) => delete panel.dataset.enter);
      tops.forEach((top) => delete top.dataset.enter);
    };

    const markEnter = (world, previous) => {
      const index = (w) => tops.findIndex((top) => top.dataset.world === w);
      const side = index(world) > index(previous) ? "right" : "left";
      panels.get(world).dataset.enter = side;
      tops[index(world)].dataset.enter = side;
    };

    const close = () => {
      clearTimeout(closeTimer);
      openWorld = null;
      clearEnter();
      panels.forEach((panel) => {
        panel.classList.remove("is-open");
        panel.style.height = "auto";
      });
      tops.forEach((top) => top.setAttribute("aria-expanded", "false"));
    };

    const open = (world) => {
      clearTimeout(closeTimer);
      if (openWorld === world) return;

      const panel = panels.get(world);
      if (!panel) return;

      const previous = openWorld;
      // Read before the outgoing panel is hidden — afterwards it has no height.
      const fromHeight = previous ? panels.get(previous).offsetHeight : 0;

      clearEnter();
      panels.forEach((other) => {
        if (other !== panel) {
          other.classList.remove("is-open");
          other.style.height = "auto";
        }
      });

      const reveal = () => {
        panel.classList.add("is-open");
        showPane(panel, 0);
      };

      if (previous) {
        // From one world straight into the other the panel is already there, so
        // the move can say "this is the other list" — the height eases to the
        // new one and the content slides in.
        withHeightTransition(panel, reveal, fromHeight);
        markEnter(world, previous);
      } else {
        // No animation on opening — the panel would otherwise unfold from zero
        // height, which says something quite different from "this list is longer".
        panel.style.height = "auto";
        reveal();
      }

      openWorld = world;
      tops.forEach((top) => {
        top.setAttribute("aria-expanded", String(top.dataset.world === world));
      });
    };

    tops.forEach((top) => {
      top.addEventListener("mouseenter", () => open(top.dataset.world));
      top.addEventListener("focus", () => open(top.dataset.world));
    });

    // A permanent entry closes an open panel the moment it is touched.
    nav.querySelectorAll(".hnav__top:not([data-world])").forEach((top) => {
      top.addEventListener("mouseenter", close);
      top.addEventListener("focus", close);
    });

    /* Is c inside the triangle p-a-b? All three edge tests carrying the same
       sign means the point sits on the same side of every edge. */
    const inTriangle = (c, p, a, b) => {
      const side = (m, n, o) =>
        (m.x - o.x) * (n.y - o.y) - (n.x - o.x) * (m.y - o.y);
      const d1 = side(c, p, a);
      const d2 = side(c, a, b);
      const d3 = side(c, b, p);
      return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
    };

    panels.forEach((panel) => {
      /* The way from a rail entry to the pane beside it runs diagonally across
         the entries underneath, and swapping on the first one the pointer
         brushes pulls the pane away mid-move. So a swap is held back while the
         pointer is still heading for the open pane — the test is the triangle
         spanned by where it was ~100ms ago and the pane's left edge. The hold
         is not a veto: if the pointer is still on the new entry when the grace
         period is up, that entry was the destination after all. */
      const AIM_GRACE = 220;
      const AIM_MIN_DX = 8;
      const AIM_WINDOW = 100;

      let aimTimer = null;
      let trail = [];

      const swap = (item) => {
        if (!item || item.dataset.on === "true") return;
        withHeightTransition(panel, () =>
          showPane(panel, Number(item.dataset.i)),
        );
      };

      const isAimingAtPane = (point) => {
        const from = trail.length > 1 ? trail[0] : null;
        // Without a sampled path there is no direction to judge — a tap, or the
        // pointer's very first move inside the panel. Those swap at once.
        if (!from || point.x - from.x < AIM_MIN_DX) return false;

        const pane = panel.querySelector(".hnav__pane:not([hidden])");
        if (!pane) return false;

        const box = pane.getBoundingClientRect();
        if (box.left <= from.x) return false;

        return inTriangle(
          point,
          from,
          { x: box.left, y: box.top },
          { x: box.left, y: box.bottom },
        );
      };

      panel.addEventListener("mousemove", (e) => {
        trail.push({ x: e.clientX, y: e.clientY, t: e.timeStamp });
        // Only the last stretch of the path counts; anything older describes a
        // move the pointer has already finished.
        while (trail.length > 2 && e.timeStamp - trail[0].t > AIM_WINDOW) {
          trail.shift();
        }
      });

      panel.addEventListener("mouseover", (e) => {
        clearTimeout(aimTimer);

        const item = e.target.closest(".hnav__rail-item");
        // Anything outside the rail means the pointer arrived — the pane it was
        // aiming for is the one already on screen, so there is nothing to hold.
        if (!item) return;

        if (!isAimingAtPane({ x: e.clientX, y: e.clientY })) {
          swap(item);
          return;
        }

        aimTimer = setTimeout(() => {
          if (item.matches(":hover")) swap(item);
        }, AIM_GRACE);
      });

      panel.addEventListener("mouseleave", () => {
        clearTimeout(aimTimer);
        trail = [];
      });

      panel.addEventListener("focusin", (e) =>
        swap(e.target.closest(".hnav__rail-item")),
      );
      panel.addEventListener("mouseenter", () => clearTimeout(closeTimer));

      // The "other area" block is a real link so it still works without this
      // script; here it opens the opposite panel instead of leaving the page.
      panel.addEventListener("click", (e) => {
        const jump = e.target.closest("[data-jump]");
        if (!jump) return;
        e.preventDefault();
        open(jump.dataset.jump);
      });
    });

    // Leaving the header runs on for a moment, so the way from the bar into the
    // panel does not break off.
    nav.addEventListener("mouseleave", () => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(close, CLOSE_DELAY);
    });

    nav.addEventListener("focusout", (e) => {
      if (nav.contains(e.relatedTarget)) return;
      close();
    });

    tops.forEach((top) => {
      top.addEventListener("keydown", (e) => {
        if (e.key !== "ArrowDown") return;
        e.preventDefault();
        open(top.dataset.world);
        const rail = panels
          .get(top.dataset.world)
          ?.querySelector(".hnav__rail-item");
        if (rail) rail.focus();
      });
    });

    // Up and down walk the rail; the focusin handler above swaps the pane.
    panels.forEach((panel) => {
      panel.addEventListener("keydown", (e) => {
        if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
        const item = e.target.closest(".hnav__rail-item");
        if (!item) return;
        e.preventDefault();
        const items = Array.from(panel.querySelectorAll(".hnav__rail-item"));
        const next =
          items[items.indexOf(item) + (e.key === "ArrowDown" ? 1 : -1)];
        if (next) next.focus();
      });
    });

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || !openWorld) return;
      const active = tops.find((top) => top.dataset.world === openWorld);
      close();
      if (active && nav.contains(document.activeElement)) active.focus();
    });
  }

  /* ------------------------------------------------------------------ *
   * Mobile drawer
   * ------------------------------------------------------------------ */

  const drawer = document.getElementById("hnav-drawer");
  if (!drawer) return;

  const stack = drawer.querySelector(".hnav-drawer__stack");
  const panes = {
    root: drawer.querySelector('[data-pane="root"]'),
    world: drawer.querySelector('[data-pane="world"]'),
    sub: drawer.querySelector('[data-pane="sub"]'),
  };
  let opener = null;
  let currentWorld = null;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  const setLevel = (level) => {
    stack.dataset.level = level;
    const pane = panes[level];
    if (!pane) return;
    pane.scrollTop = 0;
    // The target level starts at the top, not on the previous one's scroll
    // position, and takes the focus with it.
    const visible =
      pane.querySelector(":scope > .hnav-drawer__level:not([hidden])") || pane;
    const first = visible.querySelector(FOCUSABLE);
    if (first) first.focus({ preventScroll: true });
  };

  const showLevelFor = (paneName, match) => {
    const levels = panes[paneName].querySelectorAll(".hnav-drawer__level");
    let found = false;
    levels.forEach((level) => {
      const isMatch = match(level);
      level.toggleAttribute("hidden", !isMatch);
      if (isMatch) found = true;
    });
    return found;
  };

  const openWorldLevel = (world) => {
    if (!showLevelFor("world", (l) => l.dataset.world === world)) return;
    currentWorld = world;
    setLevel("world");
  };

  const openSubLevel = (index) => {
    const matched = showLevelFor(
      "sub",
      (l) => l.dataset.world === currentWorld && l.dataset.i === String(index),
    );
    if (matched) setLevel("sub");
  };

  const openDrawer = (trigger) => {
    opener = trigger || null;
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    setLevel("root");
  };

  const closeDrawer = () => {
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    setSearchOpen(false);
    if (opener) opener.focus();
    opener = null;
  };

  document.querySelectorAll("[data-hnav-open]").forEach((button) => {
    button.addEventListener("click", (e) => {
      e.preventDefault();
      openDrawer(button);
    });
  });

  drawer.querySelectorAll("[data-hnav-close]").forEach((button) => {
    button.addEventListener("click", closeDrawer);
  });

  /* The search row unfolds under the drawer head; the menu stays open. */
  const searchBar = drawer.querySelector(".hnav-drawer__search");
  const searchButtons = Array.from(drawer.querySelectorAll("[data-hnav-search]"));

  const setSearchOpen = (open) => {
    if (!searchBar) return;
    searchBar.classList.toggle("is-open", open);
    searchButtons.forEach((b) => b.setAttribute("aria-expanded", String(open)));
    if (open) {
      const input = searchBar.querySelector(".js-search-input");
      // Wait for the unfold so the focus scroll lands on a visible field.
      if (input) setTimeout(() => input.focus(), 200);
    }
  };

  searchButtons.forEach((button) => {
    button.addEventListener("click", (e) => {
      if (!searchBar) return;
      e.preventDefault();
      setSearchOpen(!searchBar.classList.contains("is-open"));
    });
  });

  /* Shopify Inbox has no public API for opening the chat; the widget's own
     launcher is clicked instead. Newer builds render it inside an open shadow
     root (`#ShopifyChat` / `inbox-online-store-chat`), older ones straight
     into `#shopify-chat`, and until the full bundle has run there is only a
     placeholder bubble. All three are tried. The loader on this site is held
     back until the first interaction, so the widget may still be on its way
     when the menu opens — hence the short retry before falling back to the
     link's own target (the contact page). */
  const findChatToggle = () => {
    const host = document.querySelector("#ShopifyChat, inbox-online-store-chat");
    const root = host && host.shadowRoot ? host.shadowRoot : null;
    return (
      (root && root.querySelector('button.chat-toggle, [data-spec="toggle-button"]')) ||
      document.querySelector("#shopify-chat button.chat-toggle, #dummy-chat-button")
    );
  };

  const openChat = (fallbackHref) => {
    const deadline = Date.now() + 3000;
    const attempt = () => {
      const toggle = findChatToggle();
      if (toggle) {
        // The launcher toggles; do not fold a chat that is already open.
        if (toggle.getAttribute("aria-expanded") !== "true") toggle.click();
        return;
      }
      if (Date.now() < deadline) {
        setTimeout(attempt, 150);
      } else if (fallbackHref) {
        window.location.href = fallbackHref;
      }
    };
    attempt();
  };

  drawer.querySelectorAll("[data-hnav-chat]").forEach((button) => {
    button.addEventListener("click", (e) => {
      e.preventDefault();
      // The drawer lies above the chat window, so it has to go first.
      closeDrawer();
      openChat(button.getAttribute("href"));
    });
  });

  panes.root.addEventListener("click", (e) => {
    const card = e.target.closest("[data-open-world]");
    if (!card) return;
    e.preventDefault();
    openWorldLevel(card.dataset.openWorld);
  });

  panes.world.addEventListener("click", (e) => {
    if (e.target.closest("[data-back]")) {
      setLevel("root");
      return;
    }
    const row = e.target.closest(".hnav-drawer__row[data-i]");
    if (!row) return;
    e.preventDefault();
    openSubLevel(Number(row.dataset.i));
  });

  panes.sub.addEventListener("click", (e) => {
    if (e.target.closest("[data-back]")) setLevel("world");
  });

  document.addEventListener("keydown", (e) => {
    if (!drawer.classList.contains("is-open")) return;

    if (e.key === "Escape") {
      if (searchBar && searchBar.classList.contains("is-open")) {
        setSearchOpen(false);
        return;
      }
      const level = stack.dataset.level;
      if (level === "sub") setLevel("world");
      else if (level === "world") setLevel("root");
      else closeDrawer();
      return;
    }

    if (e.key !== "Tab") return;

    const focusable = Array.from(drawer.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null,
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
})();
